// src/components/maps/admin/NodeCalibrationMode.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardActionArea,
  Badge,
  Collapse,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  Slide,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  MyLocation as GpsIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Room as NodeIcon,
  Stairs as StairsIcon,
  Elevator as ElevatorIcon,
  DoorFront as EntranceIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Calculate as CalculateIcon,
  Navigation as NavigationIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  ArrowBack as BackIcon,
  GpsFixed as GpsFixedIcon,
  GpsNotFixed as GpsNotFixedIcon,
  BugReport as DebugIcon,
  Warning as WarningIcon,
  Map as MapIcon,
} from '@mui/icons-material';

import indoorMappingService from '../../../services/indoorMappingService';

// Minimum calibration points needed
const MIN_CALIBRATION_POINTS = 2;

// GPS accuracy threshold (meters)
const GPS_ACCURACY_THRESHOLD = 10;

// Earth's radius for distance calculations
const EARTH_RADIUS = 6371000; // meters

/**
 * Calculate distance in meters between two GPS points
 */
const gpsDistanceMeters = (lat1, lng1, lat2, lng2) => {
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return EARTH_RADIUS * c;
};

/**
 * Calculate pixel distance between two points
 */
const pixelDistance = (x1, y1, x2, y2) => {
  return Math.sqrt((x2-x1)*(x2-x1) + (y2-y1)*(y2-y1));
};

/**
 * Calculate floor plan geo-reference from calibration points.
 *
 * This calculates the transformation needed to position the floor plan image
 * on the map so that the calibrated nodes appear at their correct GPS positions.
 *
 * Returns: { center, rotation, scale, bounds }
 */
const calculateFloorPlanGeoReference = (calibrationPoints, imageWidth, imageHeight) => {
  if (calibrationPoints.length < MIN_CALIBRATION_POINTS || !imageWidth || !imageHeight) {
    return null;
  }

  // Use calibration points
  const point1 = calibrationPoints[0];
  const point2 = calibrationPoints[1];

  // Pixel positions on floor plan
  const pixel1 = { x: point1.pixelX, y: point1.pixelY };
  const pixel2 = { x: point2.pixelX, y: point2.pixelY };

  // GPS positions
  const gps1 = { lat: point1.gpsLat, lng: point1.gpsLng };
  const gps2 = { lat: point2.gpsLat, lng: point2.gpsLng };

  // Calculate pixel vector (from point1 to point2)
  const pixelDx = pixel2.x - pixel1.x;
  const pixelDy = pixel2.y - pixel1.y;
  const pixelDist = pixelDistance(pixel1.x, pixel1.y, pixel2.x, pixel2.y);

  // Calculate GPS vector in meters
  // Positive dLat = North, positive dLng = East
  const dLat = gps2.lat - gps1.lat;
  const dLng = gps2.lng - gps1.lng;
  const latToMeters = 111320;
  const lngToMeters = 111320 * Math.cos(gps1.lat * Math.PI / 180);

  const gpsNorthMeters = dLat * latToMeters;  // positive = North
  const gpsEastMeters = dLng * lngToMeters;   // positive = East
  const gpsDist = Math.sqrt(gpsNorthMeters * gpsNorthMeters + gpsEastMeters * gpsEastMeters);

  // Calculate scale: meters per pixel
  const metersPerPixel = pixelDist > 0 ? gpsDist / pixelDist : 0.1;

  // Calculate rotation
  // Pixel angle: angle of vector from point1 to point2 in image coordinates
  // In image coords: +X is right, +Y is down
  // We want to find how much the floor plan is rotated relative to North

  // Pixel vector angle (in image coordinates, Y-down)
  // atan2(dy, dx) where +X is right, +Y is down
  const pixelAngle = Math.atan2(pixelDy, pixelDx);

  // GPS vector angle (in geographic coordinates)
  // atan2(East, North) gives angle from North, clockwise positive
  const gpsAngle = Math.atan2(gpsEastMeters, gpsNorthMeters);

  // Rotation of floor plan relative to North
  // If we walk from pixel1 to pixel2 (pixelAngle direction in image),
  // we're actually walking in gpsAngle direction in real world
  //
  // For a non-rotated floor plan (North at top):
  // - Walking right (+X, pixelAngle=0) would be East (gpsAngle=90°)
  // - Walking down (+Y, pixelAngle=90°) would be South (gpsAngle=180°)
  //
  // So: gpsAngle = pixelAngle + 90° (for non-rotated)
  // Therefore: rotation = gpsAngle - pixelAngle - 90°

  // Convert pixel angle from Y-down to standard math convention (Y-up)
  // In Y-up: angle = -pixelAngle (flip Y)
  // But we need to account for the fact that in floor plan images:
  // - Moving right (+X) in a non-rotated floor plan = East
  // - Moving down (+Y) in a non-rotated floor plan = South

  // Actually, let's think about it differently:
  // The rotation we need is the angle to rotate the floor plan image
  // so that the pixel vector aligns with the GPS vector on the map.

  // On the map: North is up (+Y in Leaflet), East is right (+X)
  // In the image: typically top is "up" (but not necessarily North), right is right

  // The rotation_degrees we save is how much to rotate the floor plan
  // so that its "up" aligns with North.

  // Pixel angle in image coords (Y-down): atan2(dy, dx)
  // To convert to Y-up: negate Y, so angle = atan2(-pixelDy, pixelDx)
  const pixelAngleYUp = Math.atan2(-pixelDy, pixelDx);

  // GPS angle on map (Y-up, North): atan2(East, North) gives angle from +Y axis
  // We want angle from +X axis for consistency: atan2(North, East)
  const gpsAngleFromEast = Math.atan2(gpsNorthMeters, gpsEastMeters);

  // Rotation = how much the floor plan image is rotated from "North at top"
  // rotation = gpsAngleFromEast - pixelAngleYUp
  let rotationRadians = gpsAngleFromEast - pixelAngleYUp;

  // Normalize to -180 to 180 degrees
  let rotationDegrees = rotationRadians * 180 / Math.PI;
  while (rotationDegrees > 180) rotationDegrees -= 360;
  while (rotationDegrees < -180) rotationDegrees += 360;

  // Now calculate the center of the floor plan in GPS coordinates
  // First, find where the image center is in pixel coordinates
  const imageCenterX = imageWidth / 2;
  const imageCenterY = imageHeight / 2;

  // Calculate offset from calibration point 1 to image center in pixels
  const offsetX = imageCenterX - pixel1.x;
  const offsetY = imageCenterY - pixel1.y;

  // Rotate this offset by the floor plan rotation and convert to meters
  // Then to GPS offset
  const cosR = Math.cos(rotationRadians);
  const sinR = Math.sin(rotationRadians);

  // In rotated floor plan coordinates:
  // - offsetX in image becomes some combination of East/North
  // - offsetY in image (positive = down = South in non-rotated) becomes some combination

  // Apply rotation: for Y-down image coordinates
  // East offset = offsetX * cos(R) - (-offsetY) * sin(R) = offsetX * cos(R) + offsetY * sin(R)
  // North offset = offsetX * sin(R) + (-offsetY) * cos(R) = offsetX * sin(R) - offsetY * cos(R)

  const eastOffsetMeters = (offsetX * cosR + offsetY * sinR) * metersPerPixel;
  const northOffsetMeters = (offsetX * sinR - offsetY * cosR) * metersPerPixel;

  // Convert to GPS degrees
  const centerLat = gps1.lat + northOffsetMeters / latToMeters;
  const centerLng = gps1.lng + eastOffsetMeters / lngToMeters;

  // Calculate bounds (before rotation - for image overlay positioning)
  // The bounds define the rectangular area the image covers
  const halfWidthMeters = (imageWidth / 2) * metersPerPixel;
  const halfHeightMeters = (imageHeight / 2) * metersPerPixel;

  const boundsNorth = centerLat + halfHeightMeters / latToMeters;
  const boundsSouth = centerLat - halfHeightMeters / latToMeters;
  const boundsEast = centerLng + halfWidthMeters / lngToMeters;
  const boundsWest = centerLng - halfWidthMeters / lngToMeters;

  return {
    center: { lat: centerLat, lng: centerLng },
    rotation: rotationDegrees,
    metersPerPixel,
    bounds: {
      north: boundsNorth,
      south: boundsSouth,
      east: boundsEast,
      west: boundsWest,
    },
    // Debug info
    debug: {
      pixelVector: { dx: pixelDx, dy: pixelDy, dist: pixelDist, angleDeg: pixelAngle * 180 / Math.PI },
      gpsVector: { east: gpsEastMeters, north: gpsNorthMeters, dist: gpsDist, angleDeg: gpsAngle * 180 / Math.PI },
      pixelAngleYUp: pixelAngleYUp * 180 / Math.PI,
      gpsAngleFromEast: gpsAngleFromEast * 180 / Math.PI,
      point1: { pixel: pixel1, gps: gps1 },
      point2: { pixel: pixel2, gps: gps2 },
      imageCenter: { x: imageCenterX, y: imageCenterY },
      offsetToCenter: { x: offsetX, y: offsetY, eastM: eastOffsetMeters, northM: northOffsetMeters },
    },
  };
};

/**
 * Convert pixel position to GPS using floor plan geo-reference
 */
const pixelToGps = (pixelX, pixelY, geoRef) => {
  if (!geoRef) return { lat: 0, lng: 0 };

  const { center, rotation, metersPerPixel, bounds } = geoRef;

  // Image dimensions from bounds
  const latToMeters = 111320;
  const lngToMeters = 111320 * Math.cos(center.lat * Math.PI / 180);

  const imageWidthMeters = (bounds.east - bounds.west) * lngToMeters;
  const imageHeightMeters = (bounds.north - bounds.south) * latToMeters;
  const imageWidth = imageWidthMeters / metersPerPixel;
  const imageHeight = imageHeightMeters / metersPerPixel;

  // Offset from image center in pixels
  const offsetX = pixelX - imageWidth / 2;
  const offsetY = pixelY - imageHeight / 2;

  // Rotate and convert to meters
  const rotRad = rotation * Math.PI / 180;
  const cosR = Math.cos(rotRad);
  const sinR = Math.sin(rotRad);

  // Apply rotation (Y-down to geographic)
  const eastMeters = (offsetX * cosR + offsetY * sinR) * metersPerPixel;
  const northMeters = (offsetX * sinR - offsetY * cosR) * metersPerPixel;

  // Convert to GPS
  return {
    lat: center.lat + northMeters / latToMeters,
    lng: center.lng + eastMeters / lngToMeters,
  };
};

// GPS Location Hook
const useGPSLocation = () => {
  const [position, setPosition] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [altitude, setAltitude] = useState(null);
  const [error, setError] = useState(null);
  const [isWatching, setIsWatching] = useState(false);
  const watchIdRef = useRef(null);
  const positionHistoryRef = useRef([]);

  const startWatching = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      return;
    }

    setIsWatching(true);
    setError(null);
    positionHistoryRef.current = [];

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy: acc, altitude: alt } = pos.coords;
        positionHistoryRef.current.push({ lat: latitude, lng: longitude, acc, alt });
        if (positionHistoryRef.current.length > 10) {
          positionHistoryRef.current.shift();
        }
        setPosition({ lat: latitude, lng: longitude });
        setAccuracy(acc);
        setAltitude(alt);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setIsWatching(false);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 1000,
        timeout: 10000,
      }
    );
  }, []);

  const stopWatching = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsWatching(false);
  }, []);

  const getAveragedPosition = useCallback(() => {
    const history = positionHistoryRef.current;
    if (history.length === 0) return null;

    const sum = history.reduce(
      (acc, pos) => ({
        lat: acc.lat + pos.lat,
        lng: acc.lng + pos.lng,
        alt: acc.alt + (pos.alt || 0),
      }),
      { lat: 0, lng: 0, alt: 0 }
    );

    return {
      lat: sum.lat / history.length,
      lng: sum.lng / history.length,
      altitude: sum.alt / history.length,
      samples: history.length,
    };
  }, []);

  useEffect(() => {
    return () => stopWatching();
  }, [stopWatching]);

  return {
    position,
    accuracy,
    altitude,
    error,
    isWatching,
    startWatching,
    stopWatching,
    getAveragedPosition,
  };
};

// Slide transition for mobile dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Main Component
const NodeCalibrationMode = ({
  buildingData,
  floorPlanId,
  onCalibrationComplete,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedFloor, setSelectedFloor] = useState(null);
  const [calibrationPoints, setCalibrationPoints] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [captureDialogOpen, setCaptureDialogOpen] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [geoReference, setGeoReference] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedFloors, setExpandedFloors] = useState({});
  const [showDebug, setShowDebug] = useState(false);

  const gps = useGPSLocation();

  // Get current floor plan
  const currentFloorPlan = selectedFloor || (floorPlanId
    ? buildingData?.floors?.find(f => f.id === floorPlanId)
    : buildingData?.floors?.[0]);

  // Initialize with first floor
  useEffect(() => {
    if (buildingData?.floors?.length > 0 && !selectedFloor) {
      const floor = floorPlanId
        ? buildingData.floors.find(f => f.id === floorPlanId)
        : buildingData.floors[0];
      setSelectedFloor(floor);
      const expanded = {};
      buildingData.floors.forEach(f => { expanded[f.id] = true; });
      setExpandedFloors(expanded);
    }
  }, [buildingData, floorPlanId, selectedFloor]);

  // Load existing calibration data
  useEffect(() => {
    const loadCalibrationData = async () => {
      if (!buildingData?.id) return;
      try {
        const existingCalibration = await indoorMappingService.getCalibrationPoints(buildingData.id);
        if (existingCalibration?.points?.length > 0) {
          setCalibrationPoints(existingCalibration.points);
        }
      } catch (err) {
        console.log('No existing calibration data');
      }
    };
    loadCalibrationData();
  }, [buildingData?.id]);

  // Recalculate geo-reference when calibration points change
  useEffect(() => {
    if (calibrationPoints.length >= MIN_CALIBRATION_POINTS && currentFloorPlan) {
      const imageWidth = currentFloorPlan.image_width || 1000;
      const imageHeight = currentFloorPlan.image_height || 1000;
      const geoRef = calculateFloorPlanGeoReference(calibrationPoints, imageWidth, imageHeight);
      setGeoReference(geoRef);
    } else {
      setGeoReference(null);
    }
  }, [calibrationPoints, currentFloorPlan]);

  // Handle node selection for calibration
  const handleSelectNode = (node, floor) => {
    setSelectedNode({ ...node, floor });
    setSelectedFloor(floor);
    setCaptureDialogOpen(true);
    gps.startWatching();
  };

  // Capture GPS position for selected node
  const handleCapturePosition = () => {
    if (!gps.position || !selectedNode) return;

    setIsCapturing(true);

    setTimeout(() => {
      const averaged = gps.getAveragedPosition();
      const capturedPosition = averaged || gps.position;

      const calibrationPoint = {
        nodeId: selectedNode.id,
        nodeName: selectedNode.name || `Node ${selectedNode.id}`,
        floorId: selectedNode.floor.id,
        floorNumber: selectedNode.floor.floor_number,
        // Pixel position on floor plan
        pixelX: parseFloat(selectedNode.x_position) || 0,
        pixelY: parseFloat(selectedNode.y_position) || 0,
        // Captured GPS position
        gpsLat: capturedPosition.lat,
        gpsLng: capturedPosition.lng,
        gpsAltitude: capturedPosition.altitude || gps.altitude,
        accuracy: gps.accuracy,
        timestamp: new Date().toISOString(),
      };

      setCalibrationPoints(prev => {
        const existingIndex = prev.findIndex(p => p.nodeId === selectedNode.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = calibrationPoint;
          return updated;
        }
        return [...prev, calibrationPoint];
      });

      setIsCapturing(false);
      setCaptureDialogOpen(false);
      gps.stopWatching();
      setSelectedNode(null);
    }, 2000);
  };

  // Remove calibration point
  const handleRemoveCalibration = (nodeId, e) => {
    if (e) e.stopPropagation();
    setCalibrationPoints(prev => prev.filter(p => p.nodeId !== nodeId));
  };

  // Apply geo-reference to floor plan
  const handleApplyCalibration = async () => {
    if (!geoReference || !currentFloorPlan) return;

    setSaving(true);
    try {
      // Save calibration points for reference
      await indoorMappingService.saveCalibrationData(buildingData.id, {
        calibrationPoints,
        appliedAt: new Date().toISOString(),
      });

      // Update floor plan with new geo-reference
      await indoorMappingService.updateFloorPlan(currentFloorPlan.id, {
        bounds_north: geoReference.bounds.north,
        bounds_south: geoReference.bounds.south,
        bounds_east: geoReference.bounds.east,
        bounds_west: geoReference.bounds.west,
        rotation_degrees: geoReference.rotation,
        scale_meters_per_pixel: geoReference.metersPerPixel,
        is_geo_referenced: true,
      });

      onCalibrationComplete?.({
        success: true,
        geoReference,
        message: 'Floor plan geo-reference updated. Refresh to see changes.',
      });
    } catch (err) {
      console.error('Failed to apply calibration:', err);
      alert('Failed to save calibration: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Get node icon based on type
  const getNodeIcon = (type) => {
    switch (type) {
      case 'stairs': return <StairsIcon />;
      case 'elevator': return <ElevatorIcon />;
      case 'entrance': return <EntranceIcon />;
      default: return <NodeIcon />;
    }
  };

  // Check if node is calibrated
  const isNodeCalibrated = (nodeId) => {
    return calibrationPoints.some(p => p.nodeId === nodeId);
  };

  // Get accuracy color
  const getAccuracyColor = (accuracy) => {
    if (accuracy <= 5) return 'success';
    if (accuracy <= 10) return 'warning';
    return 'error';
  };

  // Toggle floor expansion
  const toggleFloorExpansion = (floorId, e) => {
    if (e) e.stopPropagation();
    setExpandedFloors(prev => ({
      ...prev,
      [floorId]: !prev[floorId],
    }));
  };

  const calibratedCount = calibrationPoints.length;
  const canApply = calibratedCount >= MIN_CALIBRATION_POINTS && geoReference;

  // Count nodes with issues
  const nodesWithoutPosition = buildingData?.floors?.reduce((count, floor) => {
    return count + (floor.nodes?.filter(n => !n.x_position || !n.y_position).length || 0);
  }, 0) || 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <IconButton edge="start" onClick={onClose} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1 }}>
              GPS Calibration
            </Typography>
            <IconButton onClick={() => setShowDebug(!showDebug)} color={showDebug ? 'primary' : 'default'}>
              <DebugIcon />
            </IconButton>
            <Chip
              size="small"
              icon={<GpsIcon />}
              label={`${calibratedCount}/${MIN_CALIBRATION_POINTS}`}
              color={calibratedCount >= MIN_CALIBRATION_POINTS ? 'success' : 'default'}
            />
          </Toolbar>
        </AppBar>
      )}

      {/* Desktop Header */}
      {!isMobile && (
        <Paper sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">GPS Calibration - Position Floor Plan</Typography>
            <Box>
              <IconButton onClick={() => setShowDebug(!showDebug)} color={showDebug ? 'primary' : 'default'}>
                <DebugIcon />
              </IconButton>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<GpsIcon />}
              label={`${calibratedCount} point${calibratedCount !== 1 ? 's' : ''} captured`}
              color={calibratedCount >= MIN_CALIBRATION_POINTS ? 'success' : 'default'}
            />
            {geoReference && (
              <>
                <Chip
                  icon={<MapIcon />}
                  label={`Rotation: ${geoReference.rotation?.toFixed(1)}°`}
                  variant="outlined"
                  color={Math.abs(geoReference.rotation) < 5 ? 'default' : 'info'}
                />
                <Chip
                  icon={<CalculateIcon />}
                  label={`Scale: ${(geoReference.metersPerPixel * 100).toFixed(2)} cm/px`}
                  variant="outlined"
                />
              </>
            )}
          </Stack>
        </Paper>
      )}

      {/* Debug Panel */}
      {showDebug && geoReference && (
        <Accordion defaultExpanded sx={{ mx: 2, mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandIcon />}>
            <Typography variant="subtitle2">
              <DebugIcon sx={{ mr: 1, fontSize: 16, verticalAlign: 'middle' }} />
              Debug Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Calibration Points */}
            <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
              Calibration Points ({calibrationPoints.length})
            </Typography>
            {calibrationPoints.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 150, overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Node</TableCell>
                      <TableCell>Pixel X,Y</TableCell>
                      <TableCell>GPS Lat,Lng</TableCell>
                      <TableCell>Accuracy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {calibrationPoints.map((p, i) => (
                      <TableRow key={p.nodeId}>
                        <TableCell>{p.nodeName}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                          {p.pixelX?.toFixed(0)}, {p.pixelY?.toFixed(0)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                          {p.gpsLat?.toFixed(6)}, {p.gpsLng?.toFixed(6)}
                        </TableCell>
                        <TableCell>{p.accuracy?.toFixed(1)}m</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Calculated Geo-Reference */}
            <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
              Calculated Floor Plan Position
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, mb: 2, fontFamily: 'monospace', fontSize: 11 }}>
              <div><strong>Center:</strong> {geoReference.center?.lat?.toFixed(6)}, {geoReference.center?.lng?.toFixed(6)}</div>
              <div><strong>Rotation:</strong> {geoReference.rotation?.toFixed(1)}° from North</div>
              <div><strong>Scale:</strong> {geoReference.metersPerPixel?.toFixed(4)} m/px ({(geoReference.metersPerPixel * 100).toFixed(2)} cm/px)</div>
              <Divider sx={{ my: 0.5 }} />
              <div><strong>Bounds:</strong></div>
              <div>  North: {geoReference.bounds?.north?.toFixed(6)}</div>
              <div>  South: {geoReference.bounds?.south?.toFixed(6)}</div>
              <div>  East: {geoReference.bounds?.east?.toFixed(6)}</div>
              <div>  West: {geoReference.bounds?.west?.toFixed(6)}</div>
            </Paper>

            {/* Vector Debug */}
            {geoReference.debug && (
              <>
                <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                  Vector Analysis
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, fontFamily: 'monospace', fontSize: 11 }}>
                  <div><strong>Pixel vector:</strong> ({geoReference.debug.pixelVector?.dx?.toFixed(0)}, {geoReference.debug.pixelVector?.dy?.toFixed(0)}) = {geoReference.debug.pixelVector?.dist?.toFixed(0)}px @ {geoReference.debug.pixelVector?.angleDeg?.toFixed(1)}°</div>
                  <div><strong>GPS vector:</strong> E={geoReference.debug.gpsVector?.east?.toFixed(1)}m, N={geoReference.debug.gpsVector?.north?.toFixed(1)}m = {geoReference.debug.gpsVector?.dist?.toFixed(1)}m @ {geoReference.debug.gpsVector?.angleDeg?.toFixed(1)}° from N</div>
                  <Divider sx={{ my: 0.5 }} />
                  <div><strong>Pixel angle (Y-up):</strong> {geoReference.debug.pixelAngleYUp?.toFixed(1)}°</div>
                  <div><strong>GPS angle (from East):</strong> {geoReference.debug.gpsAngleFromEast?.toFixed(1)}°</div>
                  <div><strong>Image center:</strong> ({geoReference.debug.imageCenter?.x?.toFixed(0)}, {geoReference.debug.imageCenter?.y?.toFixed(0)})</div>
                  <div><strong>Offset to center:</strong> pixel({geoReference.debug.offsetToCenter?.x?.toFixed(0)}, {geoReference.debug.offsetToCenter?.y?.toFixed(0)}) → geo(E={geoReference.debug.offsetToCenter?.eastM?.toFixed(1)}m, N={geoReference.debug.offsetToCenter?.northM?.toFixed(1)}m)</div>
                </Paper>
              </>
            )}

            {/* Warnings */}
            {nodesWithoutPosition > 0 && (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
                {nodesWithoutPosition} nodes have no pixel position set
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Instructions */}
      <Alert
        severity="info"
        sx={{
          mx: isMobile ? 1 : 2,
          mt: isMobile ? 1 : 2,
          mb: 1,
          '& .MuiAlert-message': { fontSize: isMobile ? '0.8rem' : 'inherit' }
        }}
      >
        <AlertTitle sx={{ fontSize: isMobile ? '0.9rem' : 'inherit' }}>
          How GPS Calibration Works
        </AlertTitle>
        <Typography variant="body2">
          1. Walk to at least 2 nodes (preferably far apart, like diagonal corners)<br/>
          2. Tap each node and capture your GPS position there<br/>
          3. The system calculates the floor plan's position, rotation, and scale<br/>
          4. Click "Apply" to update the floor plan's geo-reference<br/>
          <strong>Note:</strong> This positions the floor plan image on the map - nodes stay in place on the floor plan.
        </Typography>
      </Alert>

      {/* Node List by Floor */}
      <Box sx={{ flex: 1, overflow: 'auto', px: isMobile ? 1 : 2, pb: 2 }}>
        {buildingData?.floors
          ?.sort((a, b) => (a.floor_number || 0) - (b.floor_number || 0))
          .map(floor => {
            const floorCalibrated = floor.nodes?.filter(n => isNodeCalibrated(n.id)).length || 0;
            const isExpanded = expandedFloors[floor.id] !== false;
            const calibratableNodes = floor.nodes?.filter(n => n.name || n.node_type !== 'normal') || [];

            return (
              <Card key={floor.id} sx={{ mb: 1.5 }}>
                <CardActionArea onClick={(e) => toggleFloorExpansion(floor.id, e)}>
                  <Box
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant={isMobile ? 'body1' : 'subtitle1'} fontWeight="medium">
                        {floor.floor_name}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${floorCalibrated}/${calibratableNodes.length}`}
                        color={floorCalibrated > 0 ? 'success' : 'default'}
                      />
                    </Box>
                    {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                  </Box>
                </CardActionArea>

                <Collapse in={isExpanded}>
                  <Divider />

                  {(!floor.nodes || floor.nodes.length === 0) ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography color="text.secondary" variant="body2">
                        No nodes on this floor
                      </Typography>
                    </Box>
                  ) : calibratableNodes.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography color="text.secondary" variant="body2">
                        No named nodes. Name your nodes first.
                      </Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {calibratableNodes.map(node => {
                        const calibration = calibrationPoints.find(p => p.nodeId === node.id);
                        const isCalibrated = !!calibration;

                        return (
                          <ListItemButton
                            key={node.id}
                            onClick={() => handleSelectNode(node, floor)}
                            sx={{
                              minHeight: 64,
                              bgcolor: isCalibrated ? 'success.50' : 'transparent',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              '&:last-child': { borderBottom: 'none' },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: isMobile ? 40 : 56 }}>
                              <Badge
                                badgeContent={isCalibrated ? <CheckIcon sx={{ fontSize: 10 }} /> : null}
                                color="success"
                                overlap="circular"
                              >
                                {getNodeIcon(node.node_type)}
                              </Badge>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="medium">
                                    {node.name || `${node.node_type} ${node.id}`}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary">
                                  {isCalibrated
                                    ? `✓ GPS: ${calibration.gpsLat?.toFixed(5)}, ${calibration.gpsLng?.toFixed(5)}`
                                    : `Pixel: ${node.x_position}, ${node.y_position}`}
                                </Typography>
                              }
                            />
                            {isCalibrated && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleRemoveCalibration(node.id, e)}
                                sx={{ ml: 1 }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </ListItemButton>
                        );
                      })}
                    </List>
                  )}
                </Collapse>
              </Card>
            );
          })}
      </Box>

      {/* Bottom Action Bar */}
      <Paper
        sx={{
          p: isMobile ? 1.5 : 2,
          borderTop: 1,
          borderColor: 'divider',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
        }}
        elevation={8}
      >
        {calibratedCount < MIN_CALIBRATION_POINTS && (
          <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
            <Typography variant="body2">
              Capture GPS at {MIN_CALIBRATION_POINTS - calibratedCount} more node(s)
            </Typography>
          </Alert>
        )}

        {geoReference && (
          <Alert severity="success" sx={{ mb: 1.5, py: 0.5 }}>
            <Typography variant="body2">
              <strong>Ready to apply:</strong> Floor plan will be positioned at {geoReference.center?.lat?.toFixed(5)}, {geoReference.center?.lng?.toFixed(5)} with {geoReference.rotation?.toFixed(1)}° rotation
            </Typography>
          </Alert>
        )}

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => setCalibrationPoints([])}
            disabled={calibratedCount === 0}
            size={isMobile ? 'medium' : 'large'}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleApplyCalibration}
            disabled={!canApply || saving}
            sx={{ flex: 1 }}
            size={isMobile ? 'medium' : 'large'}
          >
            {saving ? 'Saving...' : 'Apply to Floor Plan'}
          </Button>
        </Stack>
      </Paper>

      {/* GPS Capture Dialog */}
      <Dialog
        open={captureDialogOpen}
        onClose={() => {
          setCaptureDialogOpen(false);
          gps.stopWatching();
          setSelectedNode(null);
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={isMobile ? Transition : undefined}
      >
        {isMobile && (
          <AppBar position="static" color="primary">
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => {
                  setCaptureDialogOpen(false);
                  gps.stopWatching();
                  setSelectedNode(null);
                }}
              >
                <CloseIcon />
              </IconButton>
              <Typography variant="h6" sx={{ flex: 1, ml: 2 }}>
                Capture GPS
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        {!isMobile && (
          <DialogTitle>
            Calibrate: {selectedNode?.name || `Node ${selectedNode?.id}`}
          </DialogTitle>
        )}

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          {/* Node Info */}
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
            {selectedNode?.name || `Node ${selectedNode?.id}`}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            <Chip label={selectedNode?.floor?.floor_name} size="small" />
            <Chip
              label={`Pixel: ${selectedNode?.x_position}, ${selectedNode?.y_position}`}
              size="small"
              variant="outlined"
            />
          </Stack>

          {/* GPS Status */}
          {gps.error ? (
            <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
              GPS Error: {gps.error}
            </Alert>
          ) : !gps.position ? (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={80} sx={{ mb: 2 }} />
              <Typography variant="h6">Acquiring GPS...</Typography>
              <Typography color="text.secondary">
                Make sure location is enabled
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              {/* Large Accuracy Circle */}
              <Box
                sx={{
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  bgcolor: `${getAccuracyColor(gps.accuracy)}.100`,
                  border: 4,
                  borderColor: `${getAccuracyColor(gps.accuracy)}.main`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                {gps.accuracy <= GPS_ACCURACY_THRESHOLD ? (
                  <GpsFixedIcon sx={{ fontSize: 48, color: `${getAccuracyColor(gps.accuracy)}.main` }} />
                ) : (
                  <GpsNotFixedIcon sx={{ fontSize: 48, color: `${getAccuracyColor(gps.accuracy)}.main` }} />
                )}
                <Typography variant="h5" fontWeight="bold" color={`${getAccuracyColor(gps.accuracy)}.main`}>
                  ±{gps.accuracy?.toFixed(0)}m
                </Typography>
              </Box>

              <Typography variant="body1" color="text.secondary" gutterBottom>
                GPS Accuracy
              </Typography>

              {gps.accuracy > GPS_ACCURACY_THRESHOLD && (
                <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
                  Low accuracy. Try moving to an open area.
                </Alert>
              )}

              {/* Coordinates */}
              <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Detected Position
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {gps.position.lat.toFixed(7)}, {gps.position.lng.toFixed(7)}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: isMobile ? 2 : 3, pt: 0 }}>
          <Button
            onClick={() => {
              setCaptureDialogOpen(false);
              gps.stopWatching();
              setSelectedNode(null);
            }}
            size="large"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleCapturePosition}
            disabled={!gps.position || isCapturing}
            startIcon={isCapturing ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            sx={{ minWidth: 150 }}
          >
            {isCapturing ? 'Capturing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NodeCalibrationMode;
